import {
  agentViewTab,
  isScheduleRunsViewConfig,
  type AgentViewSummary,
  type ViewTab,
} from "@/api/views";
import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";
import { cacheScope } from "@/lib/ui-cache";

export interface ConversationViewSummary extends AgentViewSummary {
  agent_id: string;
  created_at: string | null;
  availability: "available" | "unavailable";
  initial_path: string | null;
  can_unpin: boolean;
  /** Additive intent metadata; it never grants a write capability. */
  file_editing_contract_version?: 1;
}

interface ConversationViewsResponse {
  views: ConversationViewSummary[];
}

interface ConversationViewResolveResponse {
  view: unknown;
}

interface ConversationViewCapabilitiesResponse {
  browser_file_pins?: unknown;
  pinned_file_editing?: unknown;
}

export interface ConversationViewCapabilities {
  browserFilePinsSupported: boolean;
  pinnedFileEditingSupported: boolean;
}

export const PINNED_FILE_EDITING_CONTRACT_VERSION = 1 as const;

export interface ConversationViewPinnedResponse {
  schema_version: 1;
  ui_event: "conversation_view.pinned";
  session_id: string;
  view_id: string;
  created: boolean;
}

export class ConversationViewsApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "ConversationViewsApiError";
  }
}

async function responseError(response: Response): Promise<ConversationViewsApiError> {
  let message = `Request failed (${response.status})`;
  try {
    const payload = await response.json();
    const detail = payload?.detail ?? payload?.error ?? payload?.message;
    if (typeof detail === "string") message = detail;
  } catch {
    // Keep the generic status when an upstream response has no JSON body.
  }
  return new ConversationViewsApiError(message, response.status);
}

function isSummary(value: unknown): value is ConversationViewSummary {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  const workflowConfig = item.config;
  const workflowFileName = (
    workflowConfig && typeof workflowConfig === "object"
      ? (workflowConfig as Record<string, unknown>).file_name
      : undefined
  );
  const hasWorkflowConfig = typeof workflowFileName === "string" && workflowFileName.trim().length > 0;
  const kanbanBoardConfig = item.config;
  const kanbanBoardProjectId = (
    kanbanBoardConfig && typeof kanbanBoardConfig === "object"
      ? (kanbanBoardConfig as Record<string, unknown>).project_id
      : undefined
  );
  const hasKanbanBoardConfig = (
    typeof kanbanBoardProjectId === "string" && kanbanBoardProjectId.trim().length > 0
  );
  const dashboardHasPrivateFields = item.type === "dashboard" && (
    [
      "config",
      "project_id",
      "root",
      "token",
      "static_url",
      "jwt",
      "expires_at",
      "bridge_origin",
      "api_base",
    ].some((key) => Object.prototype.hasOwnProperty.call(item, key))
    || (
      Object.prototype.hasOwnProperty.call(item, "initial_path")
      && item.initial_path !== null
    )
  );
  return (
    typeof item.id === "string"
    && typeof item.agent_id === "string"
    && typeof item.title === "string"
    && (
      item.type === "file_browser"
      || item.type === "code_preview_sidebar"
      || item.type === "visual_editor"
      || item.type === "dashboard"
      || item.type === "single_file"
      || item.type === "sqlite_table"
      || item.type === "wiki"
      || item.type === "workflow"
      || item.type === "kanban_board"
      || item.type === "browser_session"
      || item.type === "schedule_runs"
    )
    && typeof item.icon === "string"
    && typeof item.order === "number"
    && (item.availability === "available" || item.availability === "unavailable")
    && typeof item.can_unpin === "boolean"
    && (item.initial_path === null || typeof item.initial_path === "string")
    && (item.type !== "workflow" || hasWorkflowConfig)
    && (item.type !== "kanban_board" || hasKanbanBoardConfig)
    && (item.type !== "schedule_runs" || isScheduleRunsViewConfig(item.config))
    && !dashboardHasPrivateFields
  );
}

/** Drop unsupported additive metadata before it reaches cached view state. */
function normalizeConversationViewSummary(
  value: unknown,
): ConversationViewSummary | null {
  if (!isSummary(value)) return null;
  const summary = { ...(value as unknown as Record<string, unknown>) };
  if (
    summary.file_editing_contract_version
    !== PINNED_FILE_EDITING_CONTRACT_VERSION
  ) {
    delete summary.file_editing_contract_version;
  }
  return summary as unknown as ConversationViewSummary;
}

export function conversationViewTab(
  sessionId: string,
  view: ConversationViewSummary,
): ViewTab {
  const fileEditingContractVersion = supportedPinnedFileEditingContract(
    view.file_editing_contract_version,
  );
  return {
    id: view.id,
    title: view.title,
    type: view.type,
    icon: view.icon,
    order: view.order,
    ref: {
      scope: "conversation",
      sessionId,
      agentId: view.agent_id,
      viewId: view.id,
    },
    availability: view.availability,
    initial_path: view.initial_path,
    can_unpin: view.can_unpin,
    config: view.config,
    ...(fileEditingContractVersion === undefined
      ? {}
      : { file_editing_contract_version: fileEditingContractVersion }),
  };
}

/** Configured views stay first; conversation pins retain server insertion order. */
export function mergeConversationViews(
  agentId: string,
  staticViews: AgentViewSummary[],
  sessionId: string,
  conversationViews: ConversationViewSummary[],
): ViewTab[] {
  const configured = [...staticViews]
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
    .map((view) => agentViewTab(agentId, view));
  return [
    ...configured,
    ...conversationViews.map((view) => conversationViewTab(sessionId, view)),
  ];
}

export async function fetchConversationViews(
  sessionId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<ConversationViewSummary[]> {
  const response = await fetchWithAuth(
    `/api/sessions/${encodeURIComponent(sessionId)}/views`,
    { headers: {}, cache: "no-store", signal },
    getToken,
  );
  if (!response.ok) throw await responseError(response);
  const payload = await response.json() as ConversationViewsResponse;
  if (!Array.isArray(payload?.views)) {
    throw new ConversationViewsApiError(i18n.t("views.errors.load"), response.status);
  }
  return payload.views.flatMap((view) => {
    const normalized = normalizeConversationViewSummary(view);
    return normalized === null ? [] : [normalized];
  });
}

/**
 * Feature-detect the versioned browser-file pin contract.
 *
 * UI and agno-server can be deployed independently. Treat a missing route,
 * malformed response, or transient request failure as unsupported so callers
 * hide the action instead of sending a newer pin request to an older server.
 */
export async function isBrowserFilePinSupported(
  sessionId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<boolean> {
  const capabilities = await fetchConversationViewCapabilities(
    sessionId,
    getToken,
    signal,
  );
  return capabilities.browserFilePinsSupported;
}

/**
 * Read all additive conversation-view capabilities defensively. A server
 * older than either field remains usable for read-only view rendering.
 */
export async function fetchConversationViewCapabilities(
  sessionId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<ConversationViewCapabilities> {
  void signal;
  const key = `${cacheScope()}:${sessionId}`;
  const cached = capabilityRequests.get(key);
  if (cached && (cached.pending || Date.now() - cached.at < 30_000)) return cached.promise;
  const promise = readConversationViewCapabilities(sessionId, getToken);
  capabilityRequests.set(key, { promise, at: Date.now(), pending: true });
  void promise.finally(() => {
    const current = capabilityRequests.get(key);
    if (current?.promise === promise) capabilityRequests.set(key, { promise, at: Date.now(), pending: false });
  });
  return promise;
}

const capabilityRequests = new Map<string, { promise: Promise<ConversationViewCapabilities>; at: number; pending: boolean }>();

async function readConversationViewCapabilities(sessionId: string, getToken: AccessTokenGetter): Promise<ConversationViewCapabilities> {
  try {
    const response = await fetchWithAuth(
      `/api/sessions/${encodeURIComponent(sessionId)}/views/capabilities`,
      { headers: {}, cache: "no-store" },
      getToken,
    );
    if (!response.ok) {
      return { browserFilePinsSupported: false, pinnedFileEditingSupported: false };
    }
    const payload = await response.json() as ConversationViewCapabilitiesResponse;
    return {
      browserFilePinsSupported: (
        typeof payload?.browser_file_pins === "number"
        && Number.isInteger(payload.browser_file_pins)
        && payload.browser_file_pins >= 2
      ),
      pinnedFileEditingSupported: (
        payload?.pinned_file_editing === PINNED_FILE_EDITING_CONTRACT_VERSION
      ),
    };
  } catch {
    return { browserFilePinsSupported: false, pinnedFileEditingSupported: false };
  }
}

/** Feature-detect the additive scoped-pinned-editor contract. */
export async function isPinnedFileEditingSupported(
  sessionId: string,
  getToken: AccessTokenGetter,
  signal?: AbortSignal,
): Promise<boolean> {
  const capabilities = await fetchConversationViewCapabilities(
    sessionId,
    getToken,
    signal,
  );
  return capabilities.pinnedFileEditingSupported;
}

function supportedPinnedFileEditingContract(value: unknown): 1 | undefined {
  return value === PINNED_FILE_EDITING_CONTRACT_VERSION
    ? PINNED_FILE_EDITING_CONTRACT_VERSION
    : undefined;
}

/**
 * Resolve a signed Markdown or supported-source viewer path to an existing
 * private conversation pin. The server revalidates the path against the
 * active agent policy and returns only the safe browser summary, never the
 * canonical path.
 */
export async function resolveConversationFileView(
  sessionId: string,
  path: string,
  getToken: AccessTokenGetter,
): Promise<ConversationViewSummary | null> {
  const response = await fetchWithAuth(
    `/api/sessions/${encodeURIComponent(sessionId)}/views/resolve?path=${encodeURIComponent(path)}`,
    { headers: {}, cache: "no-store" },
    getToken,
  );
  if (!response.ok) throw await responseError(response);
  const payload = await response.json() as ConversationViewResolveResponse;
  if (payload?.view === null) return null;
  const view = normalizeConversationViewSummary(payload?.view);
  if (view === null) {
    throw new ConversationViewsApiError("Unexpected resolve response", response.status);
  }
  return view;
}

export async function deleteConversationView(
  sessionId: string,
  viewId: string,
  getToken: AccessTokenGetter,
): Promise<void> {
  const response = await fetchWithAuth(
    `/api/sessions/${encodeURIComponent(sessionId)}/views/${encodeURIComponent(viewId)}`,
    { method: "DELETE", headers: {}, cache: "no-store" },
    getToken,
  );
  if (!response.ok) throw await responseError(response);
}

function isPinnedResponse(value: unknown): value is ConversationViewPinnedResponse {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return (
    item.schema_version === 1
    && item.ui_event === "conversation_view.pinned"
    && typeof item.session_id === "string"
    && typeof item.view_id === "string"
    && typeof item.created === "boolean"
  );
}

/** Pin a browser-viewed file through the session's live server-side policy. */
export async function pinConversationFile(
  sessionId: string,
  path: string,
  getToken: AccessTokenGetter,
): Promise<ConversationViewPinnedResponse> {
  const response = await fetchWithAuth(
    `/api/sessions/${encodeURIComponent(sessionId)}/views`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      body: JSON.stringify({ path }),
    },
    getToken,
  );
  if (!response.ok) throw await responseError(response);
  const payload: unknown = await response.json();
  if (!isPinnedResponse(payload)) {
    throw new ConversationViewsApiError("Unexpected pin response", response.status);
  }
  return payload;
}
